import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { DateCell, EuroCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { CoinCell } from '@/components/ui/coin-cell';
import { usePlatformBalances } from '@/hooks/queries/system/use-platform-balances';
import { getErrorMessage } from '@/lib/api/error';
import type { ProcessedAssetRow } from '@/services/api/platform-balances';
import { formatEuros } from '@/utils/format';

const COIN_ICON_SIZE = 32;
const PERCENT_DECIMALS = 2;

const BASE_COLUMNS: ColumnDef<ProcessedAssetRow, unknown>[] = [
  { accessorKey: 'rank', header: 'Rank' },
  {
    accessorKey: 'asset',
    header: 'Asset',
    cell: ({ row }) => <CoinCell asset={row.original.asset} size={COIN_ICON_SIZE} />,
  },
  {
    accessorKey: 'percentage',
    header: 'Percentage',
    cell: ({ row }) => <span className="tabular-nums">{row.original.percentage.toFixed(PERCENT_DECIMALS)}%</span>,
  },
  {
    accessorKey: 'euroRaw',
    header: 'Value (EUR)',
    cell: ({ row }) => <EuroCell value={row.original.euroRaw} />,
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    // quantity is a decimal string — lexicographic sorting would mislead
    enableSorting: false,
    cell: ({ row }) => <span className="tabular-nums">{row.original.quantity}</span>,
  },
];

const COLUMNS_WITH_LAST_UPDATED: ColumnDef<ProcessedAssetRow, unknown>[] = [
  ...BASE_COLUMNS,
  {
    accessorKey: 'lastUpdated',
    header: 'Last Updated',
    cell: ({ row }) => <DateCell value={row.original.lastUpdated} />,
  },
];

type SectionKey = 'combined' | 'exchange' | 'instant' | 'futures';

interface SectionConfig {
  key: SectionKey;
  title: string;
  /** The All Assets tab mixes venues, so a single lastUpdated is meaningless */
  showLastUpdated: boolean;
}

const SECTIONS: SectionConfig[] = [
  { key: 'combined', title: 'All Assets', showLastUpdated: false },
  { key: 'exchange', title: 'Exchange', showLastUpdated: true },
  { key: 'instant', title: 'Instant Trade', showLastUpdated: true },
  { key: 'futures', title: 'Futures', showLastUpdated: true },
];

const DEFAULT_TAB: SectionKey = 'combined';

interface AssetSectionProps {
  title: string;
  rows: ProcessedAssetRow[] | undefined;
  total: number | undefined;
  showLastUpdated: boolean;
  isLoading: boolean;
}

const AssetSection = ({ title, rows, total, showLastUpdated, isLoading }: AssetSectionProps) => (
  <section className="flex flex-col gap-3">
    <header className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      {isLoading ? (
        <Skeleton className="h-5 w-40" />
      ) : (
        <span className="text-sm text-muted-foreground">
          Total Value: <span className="font-medium text-foreground tabular-nums">{formatEuros(total ?? 0)}</span>
        </span>
      )}
    </header>
    <DataTable columns={showLastUpdated ? COLUMNS_WITH_LAST_UPDATED : BASE_COLUMNS} data={rows} emptyMessage="No assets" isLoading={isLoading} surface={false} />
  </section>
);
AssetSection.displayName = 'AssetSection';

const AssetsPage = () => {
  const { data, isPending, isError, error, refetch } = usePlatformBalances();

  return (
    <Card className="max-w-6xl">
      <CardHeader>
        <CardTitle>Platform Asset Balances</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isError ? (
          // Deliberate fix: the legacy page rendered nothing when this request failed
          <div className="flex items-center gap-3">
            <span className="text-sm text-red-500">Failed to load: {getErrorMessage(error)}</span>
            <Button onClick={() => refetch()} size="sm" variant="outline">
              Retry
            </Button>
          </div>
        ) : null}
        {isError && !data ? null : (
          <Tabs defaultValue={DEFAULT_TAB}>
            <TabsList>
              {SECTIONS.map((section) => (
                <TabsTrigger key={section.key} value={section.key}>
                  {section.title}
                </TabsTrigger>
              ))}
            </TabsList>
            {SECTIONS.map((section) => (
              <TabsContent key={section.key} value={section.key}>
                <AssetSection isLoading={isPending} rows={data?.[section.key]} showLastUpdated={section.showLastUpdated} title={section.title} total={data?.totals[section.key]} />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
AssetsPage.displayName = 'AssetsPage';

export default AssetsPage;
